import { List, Record } from 'immutable';
import * as iots from 'io-ts';
import { array } from 'fp-ts/lib/Array';
import moment from 'moment-timezone';
import timeszonesJSON from 'moment-timezone/data/packed/latest.json';
import { either, fold } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import { ALIAS_TIMEZONES } from 'app/constants/enums/AliasTimezones';
import { parseError, decodeOrThrow, asMarshaller } from 'app/utils/ioTs';
import { identity } from 'fp-ts/lib/function';

interface ITimezone {
  name: string;
  abbrs: List<string>;
  offsets: List<number>;
  untils: List<number>;
  population: number;
}

export type Timezone = Record<ITimezone>;
// Record returns a class type, which creates a runtime variable and identifier in the namespace.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const Timezone = Record<ITimezone>({
  name: 'UTC',
  abbrs: List(),
  offsets: List(),
  untils: List(),
  population: 0,
});

const TimezoneCodec = new iots.Type<Timezone, string, string>(
  'Timezone',
  (u): u is Timezone => {
    return u instanceof Timezone;
  },
  (i, c) => {
    const zone = moment.tz.zone(i);
    if (zone === null) {
      return iots.failure(i, c, `Unable to find ${i} in moment timezones.`);
    } else {
      return iots.success(new Timezone({
        name: zone.name,
        abbrs: List(zone.abbrs),
        offsets: List(zone.offsets),
        untils: List(zone.untils),
        population: zone.population,
      }));
    }
  },
  t => t.name,
);
export const TimezoneMarshaller = asMarshaller(TimezoneCodec);

export const canonicalTimezones = timeszonesJSON.zones.map((x) => x.split('|')[0]);

export const TIMEZONES = pipe(
  // Use canonical timezone list and not full list. Timezones are extracted from a JSON file, where
  // each timezone's data is separated by '|'. We split by '|' in order to grab just the first part, the name.
  ALIAS_TIMEZONES.map(x => x.id.toString()).toArray() // enum of whitelisted non-canonical timezones
    .concat(canonicalTimezones) // canonical timezones
    .sort(), // sort again by name as one list
  a => array.map(a, TimezoneCodec.decode),
  /**
   * Sequence goes from an Array of Either<error, Timezone> to an Either<error, Timezone[]>,
   * essentially aggregating the errors and timezones in one go.
   */
  array.sequence(either),
  fold(
    (e) => {
      throw new Error(e.map(parseError).join('\n'));
    },
    identity,
  ),
  List,
).sortBy(({ name }) => (moment.tz(name).utcOffset()));

export const getTimezoneByName = (name: string) => decodeOrThrow(TimezoneCodec, name);
export const UTC = getTimezoneByName('UTC');
export const ASIA_TOKYO = getTimezoneByName('Asia/Tokyo');

export function getNameWithOffset(name: string) {
  return `(GMT ${moment.tz(name).format('Z')}) ${name}`;
}

export default TIMEZONES;
